// Vendor libraries
import cloneDeep from 'lodash.clonedeep';
import { ElementRef, NgZone } from '@angular/core';
import { formatDate } from '@angular/common';
import { Store } from '@ngrx/store';
import Highcharts from 'highcharts/highstock';
import AnnotationsAdvanced from 'highcharts/modules/annotations-advanced';
import Annotations from 'highcharts/modules/annotations';
import equal from 'NewAngular/utils/equal/deep-equal';
// Constants / utils
import { ASC, MODEL_PORTFOLIO_ID_PREFIX, STOCK, INDICATOR, COMPANY, MODEL_PORTFOLIO, FUND, MUTUAL_FUND, SEPARATE_ACCOUNT, } from 'NewAngular/constants/core.constants';
import { DATA_IN_FOOTER_OPTIONS, DATA_IN_LEGEND_OPTIONS, DEFAULT_CROSSHAIR_OPTIONS, EMBED_TYPE_IMAGE_CONTENTS, FUND_CHART_NO_DATA_HTML, FUND_CHART_OPTIONS, } from 'ChartApp/ts/features/fundamental-chart/constants';
import { CHART_PRESENTATION_MODE, NAME_IN_LEGEND_OPTIONS, PARTNER_OPTIONS, } from 'ChartApp/ts/features/charts-shared/constants';
import { FOLDERABLE_APP_CONFIG } from 'FoldersApp/constants/folderable-app-config.constants';
import { FUND_CHART_FOLDERABLE_APP_CONFIG, FUND_CHART_PAGE_CHART_OPTIONS, FUND_CHART_PAGE_CONFIGURABLE_OPTIONS, } from 'ChartApp/ts/apps/fund-chart/constants';
import { FUNDCHART_AI_SUMMARY_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { isSameDateUTC } from 'Core/redesign/utils/date';
// Services
import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { AppStateManager } from 'NewAngular/services/app-state-manager.service';
import { BaseSavedItemService } from 'NewAngular/features/components/saved-items/services/common-services/base-saved-item.service';
import { BulkActionSelectionService } from 'FoldersApp/services/bulk-action-selection.service';
import { ChartZoomService } from 'ChartApp/ts/features/charts-shared/services/chart-zoom.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';
import { FolderableCollectionServiceBase } from 'FoldersApp/services/folderable-collection-service-base.service';
import { FundChartAppStateManager } from 'ChartApp/ts/apps/fund-chart/services/fund-chart-app.service';
import { FolderExpansionService } from 'FoldersApp/services/folder-expansion-service/folder-expansion.service';
import { FundChartService } from 'ChartApp/ts/services/fund-chart.service';
import { FundChartFolderableCollectionService } from 'ChartApp/ts/services/fund-chart-folderable-collection.service';
import { ItemSelectionService } from 'NewAngular/services/item-selection.service';
import { SavedFundChartService } from 'NewAngular/features/components/saved-items/services/saved-fund-chart.service';
import { SelectedFundChartService } from 'ChartApp/ts/apps/fund-chart/services/selected-fund-chart.service';
import { SelectedFolderableService } from 'FoldersApp/services/selected-folderable.service';
import { TabOptions } from 'ChartApp/ts/interfaces/fund-chart-common.interface';
import { ChartSortColumn } from 'ChartApp/ts/enums/chart-common.enum';
import { DataInLegendOption, FundChartOptionValue } from 'ChartApp/ts/enums/fund-chart-common.enum';
// Selectors
import { getChartAnnotations } from 'ChartApp/ts/apps/fund-chart/state/reducers/chart-annotations/chart-annotations.selectors';
import { getAISummaryEvents } from 'ChartApp/ts/apps/fund-chart/state/reducers/ai-summary-events/ai-summary-events.selectors';
import { getDataInFooterDropdownConfig, getDataInLegendDropdownConfig, getFundChartDropdownConfig, getNameInLegendDropdownConfig, } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/chart-options/chart-options.selectors';
import { hasValidRequestData } from 'ChartApp/ts/features/fund-chart-page-shared/state/fund-chart.selectors';
import { getChartZoomExtremes } from 'ChartApp/ts/apps/fund-chart/state/reducers/axis-extremes/axis-extremes.selectors';
import { getSeriesLineAnnotations } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/line-annotations/line-annotations.selectors';
// Actions
import { setChartData } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/chart-data/chart-data.actions';
import { setChartOption, setDataInLegendOption, setNameInLegendOption, } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/chart-options/chart-options.actions';
import { setFundChartNote } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/note/note.actions';
import { setFundChartTitle } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/title/title.actions';
import { setFundChartType } from 'ChartApp/ts/apps/fund-chart/state/reducers/chart-type/chart-type.actions';
import { setFundChartPartner } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/partner/partner.actions';
import { clearChartAnnotations, removeChartAnnotation, setChartAnnotation, } from 'ChartApp/ts/apps/fund-chart/state/reducers/chart-annotations/chart-annotations.actions';
import { setAxisExtremes } from 'ChartApp/ts/apps/fund-chart/state/reducers/axis-extremes/axis-extremes.action';
import { deleteAIEventSummary } from 'ChartApp/ts/apps/fund-chart/state/reducers/ai-summary-events/ai-summary-events.actions';
// Components
import { FundamentalChartComponent } from 'ChartApp/ts/features/fundamental-chart/components/fundamental-chart/fundamental-chart.component';
import { LegacyAssetConversionCheckerComponent } from 'ConverterApp/features/legacy-asset-conversion-checker/legacy-asset-conversion-checker.component';
import { LineAnnotationsModalComponent } from 'ChartApp/ts/features/fund-chart-page-shared/components/line-annotations-modal/line-annotations-modal.component';
import { FundChartSeriesMenuComponent } from '../fund-chart-series-menu/fund-chart-series-menu.component';
import { FundChartAISummaryModalComponent } from '../fund-chart-ai-summary-modal/fund-chart-ai-summary-modal.component';
import * as i0 from "@angular/core";
import * as i1 from "AccountsApp/ts/services/accounts.service";
import * as i2 from "@ngrx/store";
import * as i3 from "NewAngular/features/components/saved-items/services/saved-fund-chart.service";
import * as i4 from "ChartApp/ts/apps/fund-chart/services/selected-fund-chart.service";
import * as i5 from "ChartApp/ts/apps/fund-chart/services/fund-chart-app.service";
import * as i6 from "ChartApp/ts/services/fund-chart.service";
import * as i7 from "EntitlementsApp/ts/features/feature-needed/feature.service";
import * as i8 from "ChartApp/ts/features/charts-shared/services/chart-zoom.service";
import * as i9 from "ChartApp/ts/services/fund-chart-folderable-collection.service";
import * as i10 from "@angular/common";
import * as i11 from "ChartApp/ts/features/charts-shared/components/chart-options/chart-options.component";
import * as i12 from "ChartApp/ts/features/charts-shared/components/chart-type-toggle/chart-type-toggle.component";
import * as i13 from "FoldersApp/features/folders-shared/folderable-open-saved-modal/folderable-open-saved-modal.component";
import * as i14 from "FoldersApp/features/folders-shared/folderable-rename-modal/folderable-rename-modal.component";
import * as i15 from "FoldersApp/features/folders-shared/folderable-save-as-modal/folderable-save-as-modal.component";
import * as i16 from "FoldersApp/features/folders-shared/folderable-sharing-modal/folderable-sharing-modal.component";
import * as i17 from "FoldersApp/features/folders-shared/folderable-delete-modal/folderable-delete-modal.component";
import * as i18 from "../../../../features/fundamental-chart/components/fundamental-chart/fundamental-chart.component";
import * as i19 from "../../../../features/fundamental-chart/components/fund-chart-legend/fund-chart-legend.component";
import * as i20 from "../fund-chart-ai-summary-modal/fund-chart-ai-summary-modal.component";
import * as i21 from "../../../../features/fund-chart-page-shared/components/line-annotations-modal/line-annotations-modal.component";
import * as i22 from "../../../../features/fund-chart-page-shared/components/line-annotations-tab/line-annotations-tab.component";
import * as i23 from "../../../../features/fund-chart-page-shared/components/fund-chart-view-controls/fund-chart-view-controls.component";
import * as i24 from "../../../../features/fund-chart-page-shared/components/fund-chart-correlations/fund-chart-correlations.component";
import * as i25 from "../../../../features/fund-chart-page-shared/components/fund-chart-data-format/fund-chart-data-format.component";
import * as i26 from "../../../../features/fund-chart-page-shared/components/fund-chart-list-browser/fund-chart-list-browser.component";
import * as i27 from "../../../../features/fund-chart-page-shared/components/fund-chart-panel-layout/fund-chart-panel-layout.component";
import * as i28 from "../../../../features/fund-chart-page-shared/components/fund-chart-metrics-list/fund-chart-metrics-list.component";
import * as i29 from "../../../../features/fund-chart-page-shared/components/fund-chart-securities-list/fund-chart-securities-list.component";
import * as i30 from "../../../../features/fund-chart-page-shared/components/presentation-chart/presentation-chart.component";
import * as i31 from "../../../../../../../../../ycharts/static/ts/features/directives/icon/icon.directive";
import * as i32 from "../../../../../../../../converter/static/converter/ts/features/legacy-asset-conversion-checker/legacy-asset-conversion-checker.component";
import * as i33 from "../../../../../../../../../ycharts/static/ts/features/components/loaders/loading-symbol/loading-symbol.component";
import * as i34 from "../../../../../../../../securities/static/securities/ts/features/module-upsell/module-upsell.component";
import * as i35 from "../../../../../../../../../ycharts/static/ts/features/components/page-error-message/page-error-message.component";
import * as i36 from "../../../../../../../../../ycharts/static/ts/features/components/side-menu/side-menu.component";
import * as i37 from "../../../../../../../../../ycharts/static/ts/features/components/inputs/simple-input/simple-input.component";
import * as i38 from "../../../../../../../../../ycharts/static/ts/features/components/dropdowns/single-select-dropdown/single-select-dropdown.component";
import * as i39 from "../../../../../../../../../ycharts/static/ts/features/components/toast-messages/page-toast-messages.component";
import * as i40 from "../../../../../../../../../ycharts/static/ts/features/directives/popovers/tooltip/tooltip.directive";
import * as i41 from "ChartApp/ts/apps/fund-chart/components/fund-chart-folderable-item/fund-chart-folderable-item.component";
import * as i42 from "ChartApp/ts/apps/fund-chart/components/fund-chart-folderable-open-saved-filter/fund-chart-folderable-open-saved-filter.component";
import * as i43 from "../fund-chart-header/fund-chart-header.component";
import * as i44 from "../fund-chart-series-menu/fund-chart-series-menu.component";
import * as i45 from "../fund-chart-toolset/fund-chart-toolset.component";
const _c0 = a0 => ({ container: a0 });
const _c1 = (a0, a1) => ({ "is-panel-sidebar-shown": a0, "is-panel-sidebar-hidden": a1 });
const _c2 = a0 => ({ active: a0 });
const _c3 = () => ({ button: "btn btn-secondary btn-block" });
const _c4 = a0 => ({ "cursor": a0 });
function FundChartPageComponent_ycn_fund_chart_header_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "ycn-fund-chart-header", 49);
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("aISummaries", ctx_r1.aISummaries)("securityPageType", ctx_r1.securityPageType);
} }
function FundChartPageComponent_div_50_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 26)(1, "div", 50)(2, "div", 51);
    i0.ɵɵtext(3, "Title");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "ycn-simple-input", 52);
    i0.ɵɵlistener("handler", function FundChartPageComponent_div_50_Template_ycn_simple_input_handler_4_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.setChartTitle($event)); });
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(5, "div", 53)(6, "div", 51);
    i0.ɵɵtext(7, "Notes");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(8, "ycn-simple-input", 54);
    i0.ɵɵlistener("handler", function FundChartPageComponent_div_50_Template_ycn_simple_input_handler_8_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.setChartNote($event)); });
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(9, "div", 55)(10, "ycn-single-select-dropdown", 56);
    i0.ɵɵlistener("changeHandler", function FundChartPageComponent_div_50_Template_ycn_single_select_dropdown_changeHandler_10_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.setChartPartner($event)); });
    i0.ɵɵelementEnd()();
    i0.ɵɵelement(11, "div", 38);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("value", ctx_r1.title)("isClearable", true);
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("value", ctx_r1.note)("isClearable", true);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("customCssClasses", i0.ɵɵpureFunction0(7, _c3))("selectedValue", ctx_r1.partner)("config", ctx_r1.chartSizesDropdownConfig);
} }
function FundChartPageComponent_div_55_Template(rf, ctx) { if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 57)(1, "div", 58);
    i0.ɵɵelement(2, "ycn-fund-chart-legend", 59);
    i0.ɵɵelementStart(3, "div", 60)(4, "ycn-fundamental-chart", 61);
    i0.ɵɵlistener("annotationHandler", function FundChartPageComponent_div_55_Template_ycn_fundamental_chart_annotationHandler_4_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.annotationHandler($event)); })("chartDataHandler", function FundChartPageComponent_div_55_Template_ycn_fundamental_chart_chartDataHandler_4_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.chartDataHandler($event)); })("toolsetToggleHandler", function FundChartPageComponent_div_55_Template_ycn_fundamental_chart_toolsetToggleHandler_4_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.toggleToolset($event)); })("subchartOffsetsHandler", function FundChartPageComponent_div_55_Template_ycn_fundamental_chart_subchartOffsetsHandler_4_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.subchartOffsetsHandler($event)); })("tooltipDataHandler", function FundChartPageComponent_div_55_Template_ycn_fundamental_chart_tooltipDataHandler_4_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.tooltipDataHandler($event)); })("openAISummaryClickEvent", function FundChartPageComponent_div_55_Template_ycn_fundamental_chart_openAISummaryClickEvent_4_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.openAISummaryClickEventHandler($event)); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "ycn-fund-chart-toolset", 62);
    i0.ɵɵlistener("annotationStyleChangeHandler", function FundChartPageComponent_div_55_Template_ycn_fund_chart_toolset_annotationStyleChangeHandler_5_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.updateAnnotationStyle($event)); })("cursorChangeHandler", function FundChartPageComponent_div_55_Template_ycn_fund_chart_toolset_cursorChangeHandler_5_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.changeCursor($event)); })("deleteAnnotationHandler", function FundChartPageComponent_div_55_Template_ycn_fund_chart_toolset_deleteAnnotationHandler_5_listener() { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.deleteSelectedAnnotation()); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(6, "ycn-fund-chart-series-menu", 63);
    i0.ɵɵlistener("editAnnotationHandler", function FundChartPageComponent_div_55_Template_ycn_fund_chart_series_menu_editAnnotationHandler_6_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.lineAnnotationsModal.openModal($event)); })("updateDate", function FundChartPageComponent_div_55_Template_ycn_fund_chart_series_menu_updateDate_6_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.updateSecurityEventDate($event)); })("openEventSummary", function FundChartPageComponent_div_55_Template_ycn_fund_chart_series_menu_openEventSummary_6_listener() { i0.ɵɵrestoreView(_r4); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.openSecuritysEventsModal()); });
    i0.ɵɵelementEnd()()()();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("dataInLegend", ctx_r1.dataInLegend)("tooltipData", ctx_r1.tooltipData)("subchartOffsets", ctx_r1.subchartOffsets);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngStyle", i0.ɵɵpureFunction1(18, _c4, ctx_r1.showCrosshair ? "crosshair" : "default"))("chartOptions", ctx_r1.chartOptions)("configurableOptions", ctx_r1.chartConfigurableOptions)("chartFormData", ctx_r1.fundChartFormData)("chartAnnotations", ctx_r1.chartAnnotations)("aISummaryKeys", ctx_r1.aISummaryKeys)("annotationsHidden", ctx_r1.annotationsHidden)("axisValue", ctx_r1.axisValue)("noDataClass", ctx_r1.noDataClass)("noDataHtml", ctx_r1.noDataHTML)("sidebarOpen", "" + ctx_r1.isSidebarOpen);
    i0.ɵɵadvance();
    i0.ɵɵproperty("hidden", !ctx_r1.showToolset || ctx_r1.annotationsHidden)("selectedAnnotation", ctx_r1.selectedChartAnnotation)("showCrosshair", ctx_r1.showCrosshair);
    i0.ɵɵadvance();
    i0.ɵɵproperty("showDatesOptions", ctx_r1.isSecurityEventsModalOpen && !ctx_r1.eventSummaryAsked);
} }
function FundChartPageComponent_div_56_Template(rf, ctx) { if (rf & 1) {
    const _r5 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 64)(1, "ycn-presentation-chart", 65);
    i0.ɵɵlistener("chartDataHandler", function FundChartPageComponent_div_56_Template_ycn_presentation_chart_chartDataHandler_1_listener($event) { i0.ɵɵrestoreView(_r5); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.chartDataHandler($event)); });
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("chartFormData", ctx_r1.fundChartFormData)("imageUrl", ctx_r1.imageUrl)("isFundChartCreator", false);
} }
function FundChartPageComponent_Conditional_58_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "ycn-side-menu");
} }
function FundChartPageComponent_ng_template_65_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "ycn-fund-chart-folderable-item", 66);
} if (rf & 2) {
    const folderable_r6 = ctx.folderable;
    const ancestorInfo_r7 = ctx.ancestorInfo;
    i0.ɵɵproperty("folderable", folderable_r6)("ancestorInfo", ancestorInfo_r7);
} }
function FundChartPageComponent_ng_template_67_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 67);
    i0.ɵɵtext(1, " Get Started.");
    i0.ɵɵelement(2, "br");
    i0.ɵɵelementStart(3, "span");
    i0.ɵɵtext(4, "Create a new Fundamental Chart");
    i0.ɵɵelementEnd()();
} }
function FundChartPageComponent_ng_template_69_ycn_loading_symbol_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "ycn-loading-symbol", 69);
} if (rf & 2) {
    i0.ɵɵproperty("loaderMessage", "Loading Fundamental Charts...");
} }
function FundChartPageComponent_ng_template_69_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵtemplate(0, FundChartPageComponent_ng_template_69_ycn_loading_symbol_0_Template, 1, 1, "ycn-loading-symbol", 68);
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngIf", ctx_r1.fundChartFolderableCollection.loading);
} }
function FundChartPageComponent_ng_template_71_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "ycn-fund-chart-folderable-open-saved-filter");
} }
// Annotation logic is handled in the fundamental-chart feature but annotations are
// only used in this app, so these modules are only imported here since they are quite
// hefty and we don't want them exploding the size of bundles that don't use them
Annotations(Highcharts);
AnnotationsAdvanced(Highcharts);
/**
 * Fund Chart Page Component
 *
 * This is the top-level component of the fundamental chart app.
 *
 */
export class FundChartPageComponent {
    constructor(accountsService, el, store, savedFundChartService, selectedFundChartService, fundChartAppStateService, fundChartService, featureService, ngZone, chartZoomService, fundChartFolderableCollection) {
        this.accountsService = accountsService;
        this.el = el;
        this.store = store;
        this.savedFundChartService = savedFundChartService;
        this.selectedFundChartService = selectedFundChartService;
        this.fundChartAppStateService = fundChartAppStateService;
        this.fundChartService = fundChartService;
        this.featureService = featureService;
        this.ngZone = ngZone;
        this.chartZoomService = chartZoomService;
        this.fundChartFolderableCollection = fundChartFolderableCollection;
        this.defaultOnly = false;
        this.axisValue = {};
        this.loading = false;
        this.isPresentationMode = false;
        this.isSidebarOpen = true;
        this.showToolset = true;
        this.tooltipData = [];
        this.subchartOffsets = [];
        this.chartOptions = { ...FUND_CHART_PAGE_CHART_OPTIONS };
        this.chartConfigurableOptions = {};
        this.noDataClass = 'get-started';
        this.noDataHTML = FUND_CHART_NO_DATA_HTML;
        this.imageUrl = '';
        this.note = '';
        this.title = '';
        this.partner = '';
        this.TabOptions = TabOptions;
        this.activeTab = TabOptions.INPUT;
        this.annotationsHidden = false;
        this.isFullScreen = false;
        this.USER = window.USER;
        this.fundChartDropdownConfig = {
            title: {
                label: 'Chart Options',
            },
            list: [...FUND_CHART_OPTIONS],
        };
        this.dataInLegendDropdownConfig = {
            title: {
                label: 'Data in Legend',
            },
            list: [...DATA_IN_LEGEND_OPTIONS],
        };
        this.dataInFooterDropdownConfig = {
            title: {
                label: 'Data in Footer',
            },
            list: [...DATA_IN_FOOTER_OPTIONS],
        };
        this.nameInLegendDropdownConfig = {
            title: {
                label: 'Name in Legend',
            },
            list: [...NAME_IN_LEGEND_OPTIONS],
        };
        this.chartSizesDropdownConfig = {
            title: {
                label: 'Chart Sizes',
            },
            list: PARTNER_OPTIONS,
        };
        this.dataInLegend = DataInLegendOption.VALUE;
        this.chartAnnotations = [];
        this.aISummaryKeys = [];
        this.aISummaries = [];
        this.lineAnnotations = [];
        this.showCrosshair = false;
        this.selectedChartAnnotation = null;
        this.calc = '';
        this.securityId = '';
        this.securityName = '';
        this.securityType = '';
        this.securityTopTenHoldings = '';
        this.securitySectors = '';
        this.securityAssetClasses = '';
        this.securitiesInfo = {};
        this.tooltipDate = new Date();
        this.securityEventStartDate = new Date();
        this.securityEventEndDate = new Date();
        this.isSecurityEventsModalOpen = false;
        this.eventSummaryAsked = false;
        this.hasFundChartAIFeature = this.featureService.userHasFeatures([FUNDCHART_AI_SUMMARY_FEATURE]);
        this.aISummarySecurityData = {};
        this.aISummarySecurityIDs = [];
        // Folders
        this.ChartSortColumn = ChartSortColumn;
        this.SAVED_ITEM_COLUMN_CONFIG = {
            [ChartSortColumn.NAME]: {
                label: 'name',
                numCssCols: 3,
                defaultSortDirection: ASC,
            },
            [ChartSortColumn.USER_MODIFY_DATE]: {
                label: 'date modified',
                numCssCols: 3,
                defaultSortDirection: ASC,
            },
            [ChartSortColumn.OWNER]: {
                label: 'owner',
                numCssCols: 2,
                defaultSortDirection: ASC,
            },
        };
        this.SAVED_ITEM_COLLECTION_PARAMS_DEFAULT = {
            owner: { me: true, shared_with_me: true, public: true },
            page: 1,
            sortColumn: ChartSortColumn.USER_MODIFY_DATE,
            sortDirection: ASC,
            name: '',
            viewRootFolder: null,
        };
        this.SAVED_ITEM_FILTER_DEFAULT = {
            owner: {
                me: true,
                shared_with_me: true,
                public: true,
            },
            page: 1,
            sortColumn: 'user_modify',
            sortDirection: ASC,
            name: '',
            includeEmptyFolders: true,
        };
        // Actions
        this.setChartTypeAction = setFundChartType;
        this.mapStateToComponent = (state) => {
            // Avoid displaying data and errors when user is not logged in
            if (!window.USER.authenticated) {
                return;
            }
            const { fundChart, securitiesInfo } = state;
            this.securitiesInfo = securitiesInfo;
            // Fetch AI Summary Security Data if needed
            const aISummarySecurityIds = fundChart.securities
                .map((security) => security.id)
                .filter((id) => id.startsWith(MODEL_PORTFOLIO_ID_PREFIX));
            const aISummarySecurityIdsChanged = !equal(this.aISummarySecurityIDs, aISummarySecurityIds);
            this.aISummarySecurityIDs = aISummarySecurityIds;
            const shouldFetchAISummarySecurityData = aISummarySecurityIdsChanged && this.aISummarySecurityIDs.length && this.hasFundChartAIFeature;
            if (shouldFetchAISummarySecurityData) {
                this.fundChartService.getAISummarySecurityData(this.aISummarySecurityIDs).subscribe({
                    next: (data) => {
                        this.aISummarySecurityData = data;
                    },
                    error: () => {
                        this.aISummarySecurityData = {};
                    },
                });
            }
            const chartHasValidRequestData = hasValidRequestData(state);
            this.chartConfigurableOptions = {
                ...FUND_CHART_PAGE_CONFIGURABLE_OPTIONS,
                annualizedReturns: fundChart[FundChartOptionValue.DATA_IN_LEGEND] === DataInLegendOption.ANNUALIZED_VALUE ||
                    fundChart[FundChartOptionValue.DATA_IN_LEGEND] === DataInLegendOption.VALUE_AND_ANNUALIZED_VALUE,
                format: fundChart.format,
                recessions: fundChart.recessions,
                scaleType: fundChart.scaleType,
                showLatestValueLabels: !fundChart.hideValueFlags,
            };
            this.isPresentationMode = fundChart.chartType === CHART_PRESENTATION_MODE;
            this.fundChartDropdownConfig = getFundChartDropdownConfig(state);
            this.dataInLegendDropdownConfig = getDataInLegendDropdownConfig(state);
            this.dataInFooterDropdownConfig = getDataInFooterDropdownConfig(state);
            this.nameInLegendDropdownConfig = getNameInLegendDropdownConfig(state);
            this.chartAnnotations = getChartAnnotations(state);
            this.aISummaryKeys = getAISummaryEvents(state);
            this.lineAnnotations = getSeriesLineAnnotations(state);
            this.axisValue = getChartZoomExtremes(state);
            this.fundChartFormData = chartHasValidRequestData
                ? this.fundChartService.getFlattenedFormData(fundChart)
                : undefined;
            this.imageUrl = chartHasValidRequestData
                ? this.fundChartService.getChartEmbedUrl(fundChart, EMBED_TYPE_IMAGE_CONTENTS)
                : null;
            this.note = fundChart.note;
            this.partner = fundChart.partner;
            this.dataInLegend = fundChart[FundChartOptionValue.DATA_IN_LEGEND];
            this.title = fundChart.title;
            this.validateZoomReset();
        };
        this.securityPageType = el.nativeElement.getAttribute('securityPageType');
        this.defaultOnly = el.nativeElement.getAttribute('defaultOnly') === 'True';
        this.fundChartAppStateService.initAppState();
        this.axisExtremesSubscription = this.chartZoomService
            .getAxisExtremes()
            .subscribe((axisExtremes) => {
            this.store.dispatch(setAxisExtremes({ axisExtremes }));
        });
    }
    ngOnInit() {
        // Add callback to open line annotations modal when a chart line/series is clicked
        this.chartOptions = {
            ...FUND_CHART_PAGE_CHART_OPTIONS,
            plotOptions: {
                series: {
                    cursor: 'pointer',
                    events: {
                        click: (event) => {
                            // Ignore clicks on chart annotations
                            if (event.activeAnnotation ||
                                event.target?.parentElement?.classList?.contains('highcharts-annotation-shapes')) {
                                return;
                            }
                            const lineAnnotation = this.lineAnnotations[event.point.series.index];
                            const showChartSeriesMenu = this.showChartSeriesMenu(lineAnnotation);
                            if (this.hasFundChartAIFeature && showChartSeriesMenu) {
                                this.securityId = lineAnnotation.securityId;
                                if (this.securityId in this.aISummarySecurityData) {
                                    const securityData = this.aISummarySecurityData[this.securityId];
                                    this.securityTopTenHoldings = securityData.top_ten_holdings.join(', ');
                                    this.securitySectors = securityData.sectors.join(', ');
                                    this.securityAssetClasses = securityData.asset_classes.join(', ');
                                }
                                else {
                                    this.securityTopTenHoldings = '';
                                    this.securitySectors = '';
                                    this.securityAssetClasses = '';
                                }
                                this.securityName =
                                    this.securitiesInfo[this.securityId]?.security_name ?? this.securityId;
                                if (lineAnnotation.securityType === COMPANY) {
                                    this.securityType = lineAnnotation.securityDetailedType === STOCK ? STOCK : FUND;
                                }
                                else {
                                    this.securityType = lineAnnotation.securityType;
                                }
                                this.calc = lineAnnotation.calcId;
                                this.securityEventStartDate = this.tooltipDate;
                                // Set end date a week after the selected date by default
                                const endDate = new Date(this.securityEventStartDate.getTime());
                                endDate.setDate(this.securityEventStartDate.getDate() + 7);
                                this.securityEventEndDate = endDate;
                                this.chartSeriesMenu.open(event.chartX, event.chartY, event.point.series.index);
                            }
                            else if (!this.isSecurityEventsModalOpen) {
                                this.lineAnnotationsModal.openModal(event.point.series.index);
                            }
                        },
                    },
                },
            },
        };
        // Set cursor to match user preferences
        this.accountsService.getUserPreferences().subscribe((response) => {
            if (response.show_crosshair_in_fund_chart !== this.showCrosshair) {
                this.changeCursor({ showCrosshair: response.show_crosshair_in_fund_chart }, false);
            }
        });
    }
    ngAfterViewInit() {
        this.storeSubscription = this.store.select('reducers').subscribe(this.mapStateToComponent);
        let clearSelectedAnnotation = true;
        this.ngZone.runOutsideAngular(() => {
            ['mousedown', 'touchstart'].forEach((eventName) => {
                document.addEventListener(eventName, () => {
                    // Don't clear the selected annotation if the user clicks while a modal is open
                    clearSelectedAnnotation = !document.querySelector('div.modal.show div.modal-content');
                });
            });
            ['mouseup', 'touchend'].forEach((eventName) => {
                document.addEventListener(eventName, (event) => {
                    // Clear the selected chart annotation if the user clicks
                    // outside the annotation styling toolbar
                    const annotationStylingToolbar = document.getElementById('annotation-styling-toolbar');
                    if (!annotationStylingToolbar?.contains(event.target) && clearSelectedAnnotation) {
                        this.selectedChartAnnotation = null;
                    }
                    this.chartSeriesMenu.close();
                });
            });
        });
    }
    ngOnDestroy() {
        this.storeSubscription.unsubscribe();
        this.axisExtremesSubscription.unsubscribe();
    }
    setActiveTab(e, newTab) {
        e.preventDefault();
        this.activeTab = newTab;
    }
    toggleSidebar() {
        this.isSidebarOpen = !this.isSidebarOpen;
    }
    toggleToolset(showToolset) {
        this.showToolset = showToolset;
    }
    updateChartOptions(newOption) {
        const newOptionProps = {
            label: newOption.label,
            value: newOption.value,
            selected: !!newOption.selected,
            trueValue: newOption?.trueValue,
            falseValue: newOption?.falseValue,
        };
        this.store.dispatch(setChartOption({ chartOption: newOptionProps }));
    }
    updateDataInLegend(dataInLegend) {
        this.store.dispatch(setDataInLegendOption({ dataInLegend }));
    }
    updateNameInLegend(nameInLegend) {
        this.store.dispatch(setNameInLegendOption({ nameInLegend }));
    }
    chartDataHandler(chartData) {
        if (!equal(chartData, {})) {
            this.store.dispatch(setChartData({ chartData }));
        }
        if (Object.keys(chartData).length === 0) {
            return;
        }
        this.aISummaries = chartData.aiSummaries || [];
        if (this.isSecurityEventsModalOpen && 'aiSummaries' in chartData) {
            const summaryStartDate = this.securityEventsModal.startDate();
            const findAISummary = this.findAISummaryEvent(summaryStartDate, this.securityId, this.calc);
            if (findAISummary) {
                this.securityEventsModal.setText(findAISummary.textContent, findAISummary.prompt);
            }
            else {
                this.securityEventsModal.streamSecurityEvents();
            }
        }
        else {
            // Filter event keys that dont exist in response. (interrupted or failed ai stream call)
            this.aISummaryKeys.forEach(({ calc, startDate, endDate, securityId }) => {
                const existInResponse = this.aISummaries.find((summary) => summary.calc === calc &&
                    summary.securityId === securityId &&
                    isSameDateUTC(new Date(summary.startDate), new Date(startDate)) &&
                    isSameDateUTC(new Date(summary.endDate), new Date(endDate)));
                if (!existInResponse) {
                    this.store.dispatch(deleteAIEventSummary({
                        calc,
                        startDate,
                        endDate,
                        securityId,
                    }));
                }
            });
        }
    }
    tooltipDataHandler(tooltipData) {
        if (tooltipData.length > 0) {
            this.tooltipDate = new Date(tooltipData[0][0].val1);
        }
        this.tooltipData = tooltipData;
    }
    subchartOffsetsHandler(subchartOffsets) {
        this.subchartOffsets = subchartOffsets;
    }
    setChartTitle(title) {
        this.store.dispatch(setFundChartTitle({ title }));
    }
    setChartNote(note) {
        this.store.dispatch(setFundChartNote({ note }));
    }
    setChartPartner(partner) {
        this.store.dispatch(setFundChartPartner({ partner }));
    }
    annotationHandler(fundChartAnnotation) {
        const { chartAnnotation, id } = fundChartAnnotation;
        this.selectedChartAnnotation = fundChartAnnotation;
        this.store.dispatch(setChartAnnotation({
            chartAnnotation: JSON.stringify(chartAnnotation),
            id,
        }));
    }
    updateAnnotationStyle(fundChartAnnotation) {
        const { chartAnnotation, id } = fundChartAnnotation;
        this.fundChart.updateAnnotation(chartAnnotation, id);
        this.store.dispatch(setChartAnnotation({
            chartAnnotation: JSON.stringify(chartAnnotation),
            id,
        }));
    }
    deleteSelectedAnnotation() {
        if (this.selectedChartAnnotation) {
            const { id } = this.selectedChartAnnotation;
            this.fundChart.deleteAnnotation(id);
            this.store.dispatch(removeChartAnnotation({ id }));
            this.selectedChartAnnotation = null;
        }
    }
    clearAllAnnotations() {
        this.store.dispatch(clearChartAnnotations());
    }
    changeCursor({ showCrosshair }, savePreference = true) {
        if (this.showCrosshair === showCrosshair) {
            return;
        }
        if (savePreference) {
            this.accountsService.putUserPreferences({ show_crosshair_in_fund_chart: showCrosshair }).subscribe();
        }
        this.showCrosshair = showCrosshair;
        const newChartOptions = cloneDeep(this.chartOptions);
        if (showCrosshair) {
            newChartOptions.yAxis.crosshair = {
                ...DEFAULT_CROSSHAIR_OPTIONS,
            };
            newChartOptions.xAxis.crosshair = {
                ...DEFAULT_CROSSHAIR_OPTIONS,
            };
            this.chartOptions = newChartOptions;
        }
        else {
            newChartOptions.yAxis.crosshair.width = 0;
            newChartOptions.xAxis.crosshair.width = 0;
            this.chartOptions = newChartOptions;
        }
    }
    toggleFullscreen(value) {
        this.isFullScreen = value;
        this.isSidebarOpen = !this.isFullScreen;
    }
    openSecuritysEventsModal() {
        this.isSecurityEventsModalOpen = true;
        this.securityEventsModal.questionAsked = false;
    }
    updateSecurityEventDate(typeOfDate) {
        if (typeOfDate === 'start_date') {
            this.securityEventStartDate = this.tooltipDate;
        }
        else {
            this.securityEventEndDate = this.tooltipDate;
        }
    }
    openAISummaryClickEventHandler({ selectedStartDate, seriesId, }) {
        const lastUnderscoreIdx = seriesId.lastIndexOf('_');
        const calc = seriesId.substring(0, lastUnderscoreIdx);
        const securityId = seriesId.substring(lastUnderscoreIdx + 1);
        const findSummary = this.findAISummaryEvent(selectedStartDate, securityId, calc);
        if (findSummary) {
            this.isSecurityEventsModalOpen = true;
            this.securityId = findSummary.securityId;
            this.securityName = this.securitiesInfo[findSummary.securityId]?.security_name ?? findSummary.securityId;
            this.calc = findSummary.calc;
            const formattedStartDate = formatDate(findSummary.startDate, 'yyyy-MM-dd', 'en-US');
            const formattedEndDate = formatDate(findSummary.endDate, 'yyyy-MM-dd', 'en-US');
            this.securityEventStartDate = new Date(`${formattedStartDate}T00:00`);
            this.securityEventEndDate = new Date(`${formattedEndDate}T00:00`);
            this.securityEventsModal.setText(findSummary.textContent, findSummary.prompt);
        }
    }
    addStreamedAISummary(aISummaryEvent) {
        this.aISummaries = [...this.aISummaries, aISummaryEvent];
    }
    findAISummaryEvent(startDate, securityId, calc) {
        return this.aISummaries.find((event) => event.calc === calc &&
            securityId === event.securityId &&
            isSameDateUTC(new Date(event.startDate), startDate));
    }
    validateZoomReset() {
        const chartStarted = this.fundChart?.ychartFundInteractive?.stockChart;
        let newFetchData = this.fundChartFormData;
        if (this.fundChartFormData) {
            const { chartType: _, ...fetchData } = { ...this.fundChartFormData };
            newFetchData = fetchData;
        }
        if (!equal(this.previousFundChartFormData, newFetchData) && chartStarted) {
            this.store.dispatch(setAxisExtremes({ axisExtremes: {} }));
        }
        this.previousFundChartFormData = newFetchData;
        if (this.legacyConverter) {
            this.legacyConverter.reloadDiffAndCheck(this.previousFundChartFormData?.chartId);
        }
    }
    handleAssetChecked(updated) {
        if (updated && this.getDataParams.chartId !== null) {
            this.selectedFundChartService.selectFolderable(this.getDataParams.chartId);
        }
    }
    handleAssetCopied(chartId) {
        this.selectedFundChartService.selectFolderable(parseInt(chartId, 10));
    }
    showChartSeriesMenu(lineAnnotation) {
        const { securityType, calcId } = lineAnnotation;
        const isCompanyPerformanceLine = securityType === COMPANY && ['price', 'total_return_forward_adjusted_price'].includes(calcId);
        const isMutualFundOrSMAPerformanceLine = [MUTUAL_FUND, SEPARATE_ACCOUNT].includes(securityType) &&
            ['net_asset_value', 'total_return_forward_adjusted_price'].includes(calcId);
        const isModelPortfolioPerformanceLine = securityType === MODEL_PORTFOLIO && ['level', 'total_return_forward_adjusted_price'].includes(calcId);
        const isEconomicIndicatorLine = securityType === INDICATOR;
        return (isCompanyPerformanceLine ||
            isMutualFundOrSMAPerformanceLine ||
            isModelPortfolioPerformanceLine ||
            isEconomicIndicatorLine);
    }
}
FundChartPageComponent.ɵfac = function FundChartPageComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || FundChartPageComponent)(i0.ɵɵdirectiveInject(i1.AccountsService), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i2.Store), i0.ɵɵdirectiveInject(i3.SavedFundChartService), i0.ɵɵdirectiveInject(i4.SelectedFundChartService), i0.ɵɵdirectiveInject(i5.FundChartAppStateManager), i0.ɵɵdirectiveInject(i6.FundChartService), i0.ɵɵdirectiveInject(i7.FeatureService), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i8.ChartZoomService), i0.ɵɵdirectiveInject(i9.FundChartFolderableCollectionService)); };
FundChartPageComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: FundChartPageComponent, selectors: [["ycn-fund-chart-page"]], viewQuery: function FundChartPageComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(FundamentalChartComponent, 5);
        i0.ɵɵviewQuery(LegacyAssetConversionCheckerComponent, 5);
        i0.ɵɵviewQuery(LineAnnotationsModalComponent, 5);
        i0.ɵɵviewQuery(FundChartSeriesMenuComponent, 5);
        i0.ɵɵviewQuery(FundChartAISummaryModalComponent, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.fundChart = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.legacyConverter = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.lineAnnotationsModal = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.chartSeriesMenu = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.securityEventsModal = _t.first);
    } }, standalone: false, features: [i0.ɵɵProvidersFeature([
            BulkActionSelectionService,
            ChartZoomService,
            FundChartFolderableCollectionService,
            FolderExpansionService,
            SelectedFundChartService,
            ItemSelectionService,
            { provide: AppStateManager, useExisting: FundChartAppStateManager },
            { provide: BaseSavedItemService, useExisting: SavedFundChartService },
            { provide: FolderableCollectionServiceBase, useExisting: FundChartFolderableCollectionService },
            { provide: SelectedFolderableService, useExisting: SelectedFundChartService },
            { provide: FOLDERABLE_APP_CONFIG, useValue: FUND_CHART_FOLDERABLE_APP_CONFIG },
        ])], decls: 75, vars: 63, consts: [["listItemFragment", ""], ["userHasNoDataFragment", ""], ["loadingFragment", ""], ["filterFragment", ""], [1, "page-content", "flex-row"], [1, "full-height", 3, "ngClass"], [1, "page", "page-fc"], [1, "full-height"], [3, "aISummaries", "securityPageType", 4, "ngIf"], [1, "page-body", "panel", "panel-with-sidebar", "panel-fc-chart"], [1, "panel-wrap", "row"], [1, "panel-sidebar-wrap"], [1, "panel-sidebar", "bg-panel"], [1, "tabs", "user-select-none"], [1, "nav", "nav-tabs", "nav-width-equal"], [1, "nav-item"], ["href", "", 1, "nav-link", 3, "click"], [1, "tab-content", "panel-sidebar-scroll"], [1, "tab-pane"], [1, "panel-sidebar-section"], [3, "defaultOnly"], [3, "editAnnotationHandler"], ["ycnTooltip", "", "tooltipTitle", "Hide Controls", 1, "btn", "btn-xs", "btn-circle", "btn-transparent", "btn-lightgray", "panel-sidebar-toggler", "panel-sidebar-toggler-close", 3, "click"], ["ycnIcon", "", "name", "icon-close-arrow"], [1, "panel-content-wrap"], [1, "panel-content"], [1, "row", "form-row"], ["ycnTooltip", "", "tooltipTitle", "Show Controls", 1, "btn", "btn-xs", "btn-circle", "btn-transparent", "btn-lightgray", "panel-sidebar-toggler", "panel-sidebar-toggler-open", 3, "click"], ["ycnIcon", "", "name", "icon-open-arrow"], [1, "chart-options-cover", "col-i-3"], [3, "chartOptionChangeHandler", "dataInLegendChangeHandler", "nameInLegendChangeHandler", "chartOptionsConfig", "dataInFooterDropdownConfig", "dataInLegendDropdownConfig", "nameInLegendOptionsConfig"], [1, "col", "user-select-none"], [1, "view-select-cover"], [1, "form-inline"], [1, "form-label"], [3, "setChartTypeAction"], [1, "row", "form-row", 3, "isFullScreenChange", "annotationsHiddenChange", "isFullScreen", "annotationsHidden"], ["class", "row form-row", 4, "ngIf"], [1, "row", "row-full-height"], [1, "col-12"], [1, "chart-cover"], [3, "isOpenChange", "isQuestionAsked", "streamedSummarySaved", "isOpen", "calc", "securityId", "securityName", "securityType", "securityTopTenHoldings", "securitySectors", "securityAssetClasses", "startDate", "endDate"], ["class", "chart-interactive full-height", 4, "ngIf"], ["class", "chart-presentation user-select-none", 4, "ngIf"], [3, "title"], [3, "modalHeader", "columnConfig", "sortableColumns", "userPreferenceFilterKey", "filterDefault", "listItemFragment", "filterFragment", "userHasNoDataFragment", "loadingFragment"], [3, "deleteFileErrorMessage"], [3, "modalHeader"], [3, "assetChecked", "assetCopied", "assetType"], [3, "aISummaries", "securityPageType"], [1, "col-lg-4", "chart-title-cover", "form-inline"], [1, "form-label", "user-select-none"], ["data-test-id", "enter-title", "buttonText", "Update", "placeholder", "Enter Title", 1, "input-group", 3, "handler", "value", "isClearable"], [1, "col-md-6", "col-lg-4", "chart-notes-cover", "form-inline"], ["buttonText", "Update", "placeholder", "Enter Notes", 1, "input-group", 3, "handler", "value", "isClearable"], [1, "col-md-6", "col-lg-4"], ["placeholderText", "Chart Sizes", 1, "dropdown", 3, "changeHandler", "customCssClasses", "selectedValue", "config"], [1, "chart-interactive", "full-height"], [1, "chart-item", "full-height", "overflow-hidden", "user-select-none"], [3, "dataInLegend", "tooltipData", "subchartOffsets"], [1, "chart-wrap", "full-height"], ["chartId", "chart", "data-test-id", "fund-chart", 1, "chart-canvas", "d-flex", "flex-column", 3, "annotationHandler", "chartDataHandler", "toolsetToggleHandler", "subchartOffsetsHandler", "tooltipDataHandler", "openAISummaryClickEvent", "ngStyle", "chartOptions", "configurableOptions", "chartFormData", "chartAnnotations", "aISummaryKeys", "annotationsHidden", "axisValue", "noDataClass", "noDataHtml", "sidebarOpen"], [3, "annotationStyleChangeHandler", "cursorChangeHandler", "deleteAnnotationHandler", "hidden", "selectedAnnotation", "showCrosshair"], [3, "editAnnotationHandler", "updateDate", "openEventSummary", "showDatesOptions"], [1, "chart-presentation", "user-select-none"], ["data-test-id", "presentation-chart", 3, "chartDataHandler", "chartFormData", "imageUrl", "isFundChartCreator"], [3, "folderable", "ancestorInfo"], [1, "get-started", "flex-auto"], ["data-test-id", "saved-fund-charts-loading-symbol", 3, "loaderMessage", 4, "ngIf"], ["data-test-id", "saved-fund-charts-loading-symbol", 3, "loaderMessage"]], template: function FundChartPageComponent_Template(rf, ctx) { if (rf & 1) {
        const _r1 = i0.ɵɵgetCurrentView();
        i0.ɵɵelement(0, "ycn-page-error-message");
        i0.ɵɵelementStart(1, "main", 4)(2, "div", 5)(3, "div", 6)(4, "div", 7);
        i0.ɵɵtemplate(5, FundChartPageComponent_ycn_fund_chart_header_5_Template, 1, 2, "ycn-fund-chart-header", 8);
        i0.ɵɵelementStart(6, "div", 9)(7, "div", 10)(8, "div", 11)(9, "div", 12)(10, "div", 13)(11, "ul", 14)(12, "li", 15)(13, "a", 16);
        i0.ɵɵlistener("click", function FundChartPageComponent_Template_a_click_13_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.setActiveTab($event, ctx.TabOptions.INPUT)); });
        i0.ɵɵtext(14, " Input ");
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(15, "li", 15)(16, "a", 16);
        i0.ɵɵlistener("click", function FundChartPageComponent_Template_a_click_16_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.setActiveTab($event, ctx.TabOptions.ANNOTATE)); });
        i0.ɵɵtext(17, " Annotate ");
        i0.ɵɵelementEnd()()()();
        i0.ɵɵelementStart(18, "div", 17)(19, "div", 18)(20, "section", 19);
        i0.ɵɵelement(21, "ycn-fund-chart-securities-list");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(22, "section", 19);
        i0.ɵɵelement(23, "ycn-fund-chart-metrics-list", 20);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(24, "section", 19);
        i0.ɵɵelement(25, "ycn-fund-chart-list-browser");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(26, "section", 19);
        i0.ɵɵelement(27, "ycn-fund-chart-data-format");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(28, "section", 19);
        i0.ɵɵelement(29, "ycn-fund-chart-panel-layout");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(30, "section", 19);
        i0.ɵɵelement(31, "ycn-fund-chart-correlations", 20);
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(32, "div", 18)(33, "ycn-line-annotations-tab", 21);
        i0.ɵɵlistener("editAnnotationHandler", function FundChartPageComponent_Template_ycn_line_annotations_tab_editAnnotationHandler_33_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.lineAnnotationsModal.openModal($event)); });
        i0.ɵɵelementEnd()()();
        i0.ɵɵelementStart(34, "div", 22);
        i0.ɵɵlistener("click", function FundChartPageComponent_Template_div_click_34_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.toggleSidebar()); });
        i0.ɵɵnamespaceSVG();
        i0.ɵɵelement(35, "svg", 23);
        i0.ɵɵelementEnd()()();
        i0.ɵɵnamespaceHTML();
        i0.ɵɵelementStart(36, "div", 24)(37, "div", 25)(38, "div", 26)(39, "div", 27);
        i0.ɵɵlistener("click", function FundChartPageComponent_Template_div_click_39_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.toggleSidebar()); });
        i0.ɵɵnamespaceSVG();
        i0.ɵɵelement(40, "svg", 28);
        i0.ɵɵelementEnd();
        i0.ɵɵnamespaceHTML();
        i0.ɵɵelementStart(41, "div", 29)(42, "ycn-chart-options", 30);
        i0.ɵɵlistener("chartOptionChangeHandler", function FundChartPageComponent_Template_ycn_chart_options_chartOptionChangeHandler_42_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.updateChartOptions($event)); })("dataInLegendChangeHandler", function FundChartPageComponent_Template_ycn_chart_options_dataInLegendChangeHandler_42_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.updateDataInLegend($event)); })("nameInLegendChangeHandler", function FundChartPageComponent_Template_ycn_chart_options_nameInLegendChangeHandler_42_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.updateNameInLegend($event)); });
        i0.ɵɵelementEnd()();
        i0.ɵɵelementStart(43, "div", 31)(44, "div", 32)(45, "div", 33)(46, "div", 34);
        i0.ɵɵtext(47, "Presentation View");
        i0.ɵɵelementEnd();
        i0.ɵɵelement(48, "ycn-chart-type-toggle", 35);
        i0.ɵɵelementEnd()()()();
        i0.ɵɵelementStart(49, "ycn-fund-chart-view-controls", 36);
        i0.ɵɵlistener("isFullScreenChange", function FundChartPageComponent_Template_ycn_fund_chart_view_controls_isFullScreenChange_49_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.toggleFullscreen($event)); });
        i0.ɵɵtwoWayListener("annotationsHiddenChange", function FundChartPageComponent_Template_ycn_fund_chart_view_controls_annotationsHiddenChange_49_listener($event) { i0.ɵɵrestoreView(_r1); i0.ɵɵtwoWayBindingSet(ctx.annotationsHidden, $event) || (ctx.annotationsHidden = $event); return i0.ɵɵresetView($event); });
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(50, FundChartPageComponent_div_50_Template, 12, 8, "div", 37);
        i0.ɵɵelementStart(51, "div", 38)(52, "div", 39)(53, "div", 40)(54, "ycn-fund-chart-ai-summary-modal", 41);
        i0.ɵɵtwoWayListener("isOpenChange", function FundChartPageComponent_Template_ycn_fund_chart_ai_summary_modal_isOpenChange_54_listener($event) { i0.ɵɵrestoreView(_r1); i0.ɵɵtwoWayBindingSet(ctx.isSecurityEventsModalOpen, $event) || (ctx.isSecurityEventsModalOpen = $event); return i0.ɵɵresetView($event); });
        i0.ɵɵlistener("isQuestionAsked", function FundChartPageComponent_Template_ycn_fund_chart_ai_summary_modal_isQuestionAsked_54_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.eventSummaryAsked = $event); })("streamedSummarySaved", function FundChartPageComponent_Template_ycn_fund_chart_ai_summary_modal_streamedSummarySaved_54_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.addStreamedAISummary($event)); });
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(55, FundChartPageComponent_div_55_Template, 7, 20, "div", 42)(56, FundChartPageComponent_div_56_Template, 2, 3, "div", 43);
        i0.ɵɵelementEnd()()()()()();
        i0.ɵɵelement(57, "ycn-module-upsell", 44);
        i0.ɵɵelementEnd()()()();
        i0.ɵɵconditionalCreate(58, FundChartPageComponent_Conditional_58_Template, 1, 0, "ycn-side-menu");
        i0.ɵɵelementEnd();
        i0.ɵɵelement(59, "ycn-folderable-open-saved-modal", 45)(60, "ycn-folderable-delete-modal", 46)(61, "ycn-folderable-rename-modal")(62, "ycn-folderable-sharing-modal")(63, "ycn-page-toast-messages")(64, "ycn-folderable-save-as-modal", 47);
        i0.ɵɵtemplate(65, FundChartPageComponent_ng_template_65_Template, 1, 2, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor)(67, FundChartPageComponent_ng_template_67_Template, 5, 0, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor)(69, FundChartPageComponent_ng_template_69_Template, 1, 1, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor)(71, FundChartPageComponent_ng_template_71_Template, 1, 0, "ng-template", null, 3, i0.ɵɵtemplateRefExtractor);
        i0.ɵɵelementStart(73, "ycn-legacy-asset-conversion-checker", 48);
        i0.ɵɵlistener("assetChecked", function FundChartPageComponent_Template_ycn_legacy_asset_conversion_checker_assetChecked_73_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleAssetChecked($event)); })("assetCopied", function FundChartPageComponent_Template_ycn_legacy_asset_conversion_checker_assetCopied_73_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.handleAssetCopied($event)); });
        i0.ɵɵelementEnd();
        i0.ɵɵelement(74, "ycn-line-annotations-modal");
    } if (rf & 2) {
        const listItemFragment_r8 = i0.ɵɵreference(66);
        const userHasNoDataFragment_r9 = i0.ɵɵreference(68);
        const loadingFragment_r10 = i0.ɵɵreference(70);
        const filterFragment_r11 = i0.ɵɵreference(72);
        i0.ɵɵadvance();
        i0.ɵɵclassProp("chart-fullscreen", ctx.isFullScreen);
        i0.ɵɵadvance();
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(50, _c0, !ctx.securityPageType));
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("ngIf", !ctx.isFullScreen && ctx.USER.authenticated);
        i0.ɵɵadvance();
        i0.ɵɵclassMap(i0.ɵɵpureFunction2(52, _c1, ctx.isSidebarOpen, !ctx.isSidebarOpen));
        i0.ɵɵadvance(7);
        i0.ɵɵclassMap(i0.ɵɵpureFunction1(55, _c2, ctx.activeTab === ctx.TabOptions.INPUT));
        i0.ɵɵadvance(3);
        i0.ɵɵclassMap(i0.ɵɵpureFunction1(57, _c2, ctx.activeTab === ctx.TabOptions.ANNOTATE));
        i0.ɵɵadvance(3);
        i0.ɵɵclassMap(i0.ɵɵpureFunction1(59, _c2, ctx.activeTab === ctx.TabOptions.INPUT));
        i0.ɵɵadvance(4);
        i0.ɵɵproperty("defaultOnly", ctx.defaultOnly);
        i0.ɵɵadvance(8);
        i0.ɵɵproperty("defaultOnly", ctx.defaultOnly);
        i0.ɵɵadvance();
        i0.ɵɵclassMap(i0.ɵɵpureFunction1(61, _c2, ctx.activeTab === ctx.TabOptions.ANNOTATE));
        i0.ɵɵadvance(10);
        i0.ɵɵproperty("chartOptionsConfig", ctx.fundChartDropdownConfig)("dataInFooterDropdownConfig", ctx.dataInFooterDropdownConfig)("dataInLegendDropdownConfig", ctx.dataInLegendDropdownConfig)("nameInLegendOptionsConfig", ctx.nameInLegendDropdownConfig);
        i0.ɵɵadvance(6);
        i0.ɵɵproperty("setChartTypeAction", ctx.setChartTypeAction);
        i0.ɵɵadvance();
        i0.ɵɵproperty("isFullScreen", ctx.isFullScreen);
        i0.ɵɵtwoWayProperty("annotationsHidden", ctx.annotationsHidden);
        i0.ɵɵadvance();
        i0.ɵɵproperty("ngIf", ctx.isPresentationMode);
        i0.ɵɵadvance(4);
        i0.ɵɵtwoWayProperty("isOpen", ctx.isSecurityEventsModalOpen);
        i0.ɵɵproperty("calc", ctx.calc)("securityId", ctx.securityId)("securityName", ctx.securityName)("securityType", ctx.securityType)("securityTopTenHoldings", ctx.securityTopTenHoldings)("securitySectors", ctx.securitySectors)("securityAssetClasses", ctx.securityAssetClasses)("startDate", ctx.securityEventStartDate)("endDate", ctx.securityEventEndDate);
        i0.ɵɵadvance();
        i0.ɵɵproperty("ngIf", !ctx.isPresentationMode);
        i0.ɵɵadvance();
        i0.ɵɵproperty("ngIf", ctx.isPresentationMode);
        i0.ɵɵadvance();
        i0.ɵɵproperty("title", "Fundamental Chart");
        i0.ɵɵadvance();
        i0.ɵɵconditional(!ctx.isFullScreen && ctx.USER.authenticated && !ctx.securityPageType ? 58 : -1);
        i0.ɵɵadvance();
        i0.ɵɵproperty("modalHeader", "Open Saved Fundamental Chart")("columnConfig", ctx.SAVED_ITEM_COLUMN_CONFIG)("sortableColumns", ctx.ChartSortColumn)("userPreferenceFilterKey", "fund_chart_list_modal_filters")("filterDefault", ctx.SAVED_ITEM_FILTER_DEFAULT)("listItemFragment", listItemFragment_r8)("filterFragment", filterFragment_r11)("userHasNoDataFragment", userHasNoDataFragment_r9)("loadingFragment", loadingFragment_r10);
        i0.ɵɵadvance();
        i0.ɵɵproperty("deleteFileErrorMessage", "There was an error deleting the fundamental chart.");
        i0.ɵɵadvance(4);
        i0.ɵɵproperty("modalHeader", "Save Fundamental Chart As");
        i0.ɵɵadvance(9);
        i0.ɵɵproperty("assetType", "fund_chart");
    } }, dependencies: [i10.NgClass, i10.NgIf, i10.NgStyle, i11.ChartOptionsComponent, i12.ChartTypeToggleComponent, i13.FolderableOpenSavedModalComponent, i14.FolderableRenameModalComponent, i15.FolderableSaveAsModalComponent, i16.FolderableSharingModalComponent, i17.FolderableDeleteModalComponent, i18.FundamentalChartComponent, i19.FundChartLegendComponent, i20.FundChartAISummaryModalComponent, i21.LineAnnotationsModalComponent, i22.LineAnnotationsTabComponent, i23.FundChartViewControlsComponent, i24.FundChartCorrelationsComponent, i25.FundChartDataFormatComponent, i26.FundChartListBrowserComponent, i27.FundChartPanelLayoutComponent, i28.FundChartMetricsListComponent, i29.FundChartSecuritiesListComponent, i30.PresentationChartComponent, i31.IconDirective, i32.LegacyAssetConversionCheckerComponent, i33.LoadingSymbolComponent, i34.ModuleUpsellComponent, i35.PageErrorMessageComponent, i36.SideMenuComponent, i37.SimpleInputComponent, i38.SingleSelectDropdownComponent, i39.PageToastMessagesComponent, i40.TooltipDirective, i41.FundChartFolderableItemComponent, i42.FundChartFolderableOpenSavedFilterComponent, i43.FundChartHeaderComponent, i44.FundChartSeriesMenuComponent, i45.FundChartToolsetComponent], encapsulation: 2 });
